import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Globe2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import LegacyCompanySetupPage from './LegacyCompanySetupPage';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';
import {
  companyOnboardingApi,
  applyOnboardingCompletion,
  ContextApiError,
  emptyCompanyProfile,
  isResearching,
  isOlderDraft,
  type CompanyBusinessProfile,
  type OnboardingCapabilities,
  type OnboardingDraft,
} from '../company-context/api';
import { BusinessBrief, ProfileEditor } from '../company-context/ProfileEditor';
import QuestionCard from '../company-context/QuestionCard';
import SourceLibrary from '../company-context/SourceLibrary';
import ResearchProgress from '../company-context/ResearchProgress';
import './DashboardPage.css';
import '../company-context/CompanyContext.css';

const steps = ['Import company context', 'Fill the important gaps', 'Review your company brief'];

export default function CompanySetupPage() {
  const [capabilities, setCapabilities] = useState<OnboardingCapabilities | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    companyOnboardingApi
      .capabilities()
      .then((value) => {
        if (active) setCapabilities(value);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  if (capabilities && !capabilities.enabled) return <LegacyCompanySetupPage />;
  if (!capabilities)
    return (
      <div className="company-onboarding context-loading">
        {error ? (
          <>
            <h1>Let’s reconnect company setup.</h1>
            <p role="alert">{error}</p>
            <button
              type="button"
              className="context-button"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Try again
            </button>
            <Link to="/dashboard">Back to companies</Link>
          </>
        ) : (
          <>
            <Loader2 className="context-spin" size={26} />
            <p role="status">Preparing company setup…</p>
          </>
        )}
      </div>
    );
  return <CompanyOnboardingWizard capabilities={capabilities} />;
}

export function CompanyOnboardingWizard({
  capabilities,
  initialDraft,
  onApprove,
}: {
  capabilities: OnboardingCapabilities;
  initialDraft?: OnboardingDraft;
  onApprove?: (draft: OnboardingDraft) => Promise<void>;
}) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const practiceId = initialDraft?.practiceId || params.get('practice') || undefined;
  const pointerKey = `stratplan.onboarding.${(window as any).Clerk?.user?.id || 'signed-in'}.${practiceId || 'company'}`;
  const [step, setStep] = useState(() => {
    if (initialDraft) return 3;
    let storedStep = '';
    try {
      storedStep = localStorage.getItem(`${pointerKey}.step`) || '';
    } catch {
      /* Use URL or first step. */
    }
    return Math.max(1, Math.min(3, Number(params.get('step') || storedStep) || 1));
  });
  const [draft, setDraft] = useState<OnboardingDraft | null>(initialDraft || null);
  const [profile, setProfile] = useState<CompanyBusinessProfile>(
    initialDraft?.profile || { ...emptyCompanyProfile }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<'saved' | 'pending' | 'saving' | 'failed'>('saved');
  const [answer, setAnswer] = useState(initialDraft?.pendingAnswer?.value || '');
  const [ownerEmail, setOwnerEmail] = useState(initialDraft?.ownerEmail || '');
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [manual, setManual] = useState(false);
  const [savedDrafts, setSavedDrafts] = useState<OnboardingDraft[]>([]);
  const draftRef = useRef(draft);
  const profileRef = useRef(profile);
  const ownerEmailRef = useRef(ownerEmail);
  const pendingAnswerRef = useRef<{ questionId: string; value: string } | null>(
    initialDraft?.pendingAnswer || null
  );
  const pollingError = useRef<string | null>(null);
  const saveFailed = useRef(false);
  const editVersion = useRef(0);
  const dirty = useRef(false);
  const alive = useRef(true);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const initialization = useRef<Promise<OnboardingDraft> | null>(null);
  const enqueue = <T,>(operation: () => Promise<T>): Promise<T> => {
    const result = queue.current.then(operation, operation);
    queue.current = result.catch(() => undefined);
    return result;
  };
  const accept = (next: OnboardingDraft, replaceProfile = false, preserveProfile = false) => {
    if (isOlderDraft(draftRef.current, next)) return;
    if (replaceProfile && dirty.current && draftRef.current?.id === next.id) preserveProfile = true;
    draftRef.current = next;
    if (!alive.current) return;
    setDraft(next);
    if (replaceProfile && !preserveProfile) {
      ownerEmailRef.current = next.ownerEmail || '';
      setOwnerEmail(ownerEmailRef.current);
      pendingAnswerRef.current = next.pendingAnswer || null;
    }
    if (!preserveProfile && (replaceProfile || !dirty.current)) {
      profileRef.current = { ...emptyCompanyProfile, ...next.profile };
      setProfile(profileRef.current);
    }
  };
  const saveNow = async () => {
    if (!dirty.current || !draftRef.current) return draftRef.current;
    const sent = profileRef.current;
    const version = editVersion.current;
    setSaving('saving');
    saveFailed.current = false;
    try {
      const next = await companyOnboardingApi.save(
        draftRef.current.id,
        draftRef.current.revision,
        sent,
        draftRef.current.mode === 'adviser' ? ownerEmailRef.current : undefined,
        pendingAnswerRef.current
      );
      if (version === editVersion.current) {
        dirty.current = false;
        setSaving('saved');
      }
      accept(next, false, true);
      return next;
    } catch (err) {
      saveFailed.current = true;
      setSaving('failed');
      throw err;
    }
  };
  const perform = async (
    action: (current: OnboardingDraft) => Promise<OnboardingDraft | void>,
    save = true
  ) => {
    setBusy(true);
    setError('');
    try {
      await enqueue(async () => {
        if (save) await saveNow();
        if (!draftRef.current) throw new Error('Your company draft is still loading.');
        const result = await action(draftRef.current);
        if (result) accept(result);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update your company draft.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const changeProfile = (next: CompanyBusinessProfile) => {
    editVersion.current += 1;
    dirty.current = true;
    profileRef.current = next;
    setProfile(next);
    setSaving('pending');
    setConfirmed(false);
  };
  const goStep = (next: number) => {
    setStep(next);
    setError('');
    try {
      localStorage.setItem(`${pointerKey}.step`, String(next));
    } catch {
      /* URL is still updated. */
    }
    if (!initialDraft)
      setParams(
        (current) => {
          current.set('step', String(next));
          return current;
        },
        { replace: true }
      );
  };
  useEffect(() => {
    alive.current = true;
    if (initialDraft)
      return () => {
        alive.current = false;
      };
    const explicitId = params.get('draft');
    if (!initialization.current)
      initialization.current = (async () => {
        if (explicitId) return companyOnboardingApi.get(explicitId);
        let storedId: string | null = null;
        try {
          storedId = localStorage.getItem(pointerKey);
        } catch {
          /* Server draft is still saved. */
        }
        if (storedId) {
          try {
            const existing = await companyOnboardingApi.get(storedId);
            if (existing.status !== 'completed') return existing;
          } catch (err) {
            if (!(err instanceof ContextApiError) || ![403, 404, 410].includes(err.status))
              throw err;
          }
        }
        setStep(1);
        return companyOnboardingApi.create({
          mode: practiceId ? 'adviser' : 'company',
          practiceId,
        });
      })();
    initialization.current
      .then((next) => {
        if (!alive.current) return;
        accept(next, true);
        try {
          localStorage.setItem(pointerKey, next.id);
        } catch {
          /* URL also supports resume. */
        }
        setParams(
          (current) => {
            current.set('draft', next.id);
            return current;
          },
          { replace: true }
        );
      })
      .catch(async (err) => {
        if (!alive.current) return;
        setError(err.message);
        initialization.current = null;
        if (err instanceof ContextApiError && [409, 429].includes(err.status)) {
          try {
            const drafts = await companyOnboardingApi.list();
            if (alive.current) setSavedDrafts(drafts);
          } catch {
            /* Preserve the original actionable error. */
          }
        }
      });
    return () => {
      alive.current = false;
    };
  }, [attempt]);
  useEffect(() => {
    if (!dirty.current || !draft) return;
    const timer = setTimeout(() => {
      void enqueue(saveNow).catch((err) => {
        if (alive.current) setError(err.message);
      });
    }, 750);
    return () => clearTimeout(timer);
  }, [profile, ownerEmail, answer, draft?.id]);
  useEffect(() => {
    if (!isResearching(draft)) return;
    const timer = setInterval(() => {
      void enqueue(async () => {
        if (draftRef.current) {
          accept(await companyOnboardingApi.get(draftRef.current.id));
          if (pollingError.current) {
            const previousError = pollingError.current;
            setError((current) => (current === previousError ? '' : current));
            pollingError.current = null;
          }
        }
      }).catch((err) => {
        if (alive.current && !saveFailed.current) {
          pollingError.current =
            err.message === 'Failed to fetch'
              ? 'We couldn’t reach StratPlan. We’ll retry while your draft stays saved.'
              : err.message;
          setError(pollingError.current!);
        }
      });
    }, 1800);
    return () => clearInterval(timer);
  }, [draft?.id, draft?.status]);
  useEffect(() => {
    const pending =
      draft?.pendingAnswer?.questionId === draft?.interview?.currentQuestion?.id
        ? draft?.pendingAnswer
        : null;
    pendingAnswerRef.current = pending || null;
    setAnswer(pending?.value || '');
  }, [draft?.interview?.currentQuestion?.id]);
  useEffect(() => {
    if (draft?.status === 'completed') {
      try {
        localStorage.removeItem(pointerKey);
        localStorage.removeItem(`${pointerKey}.step`);
      } catch {
        /* No persistent pointer. */
      }
    }
    if (draft?.status === 'completed' && draft.mode === 'company' && draft.result?.companyId)
      navigate(`/company/${draft.result.companyId}`, { replace: true });
  }, [draft?.status]);
  const startInterview = async () => {
    await perform(async (current) => {
      const next =
        current.interview.currentQuestion || current.interview.complete
          ? current
          : await companyOnboardingApi.nextQuestion(current.id, current.revision);
      goStep(2);
      return next;
    });
  };
  const result = draft?.result;
  const invitation = result?.acceptUrl || result?.ownerInviteUrl;
  const validProfile = profile.name.trim().length >= 2 && Boolean(profile.description.trim());
  const researching = isResearching(draft);
  const isExisting = draft?.mode === 'existing';
  const backUrl = isExisting
    ? `/company/${draft?.companyId}/context`
    : practiceId
      ? `/adviser?practice=${encodeURIComponent(practiceId)}&tab=clients`
      : '/dashboard';
  return (
    <div className={`company-onboarding ${isExisting ? 'company-onboarding-embedded' : ''}`}>
      {!isExisting && (
        <header className="context-setup-header">
          <Link to={backUrl} aria-label="StratPlan companies">
            <img src={CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT} alt="StratPlan" />
          </Link>
          <Link to={backUrl} className="context-back-link">
            <ArrowLeft size={16} /> {practiceId ? 'Back to clients' : 'Back to companies'}
          </Link>
        </header>
      )}
      {draft?.status === 'completed' && result ? (
        <main className="context-complete-card">
          <span className="context-success-icon">
            <CheckCircle2 size={34} />
          </span>
          <p className="context-eyebrow">Workspace ready</p>
          <h1>{profile.name} is ready for its next chapter.</h1>
          <p>
            Your reviewed company brief is saved and ready to support more specific strategy and AI
            suggestions.
          </p>
          {invitation && (
            <div className="context-invitation">
              <h2>Invite the client owner</h2>
              <p>
                Share this private link with {ownerEmail || 'the client owner'}. They accept direct
                ownership while your practice keeps its delegated access.
              </p>
              <input
                aria-label="Client ownership invitation link"
                readOnly
                value={invitation}
                onFocus={(event) => event.target.select()}
              />
              <button
                type="button"
                className="context-button context-button-secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(invitation);
                    setCopied(true);
                  } catch {
                    setError('Select and copy the invitation link above.');
                  }
                }}
              >
                <Copy size={16} /> {copied ? 'Copied' : 'Copy invitation link'}
              </button>
            </div>
          )}
          {error && (
            <p role="alert" className="context-error">
              {error}
            </p>
          )}
          <div className="context-actions">
            <Link className="context-button" to={`/company/${result.companyId}`}>
              Open {practiceId ? 'client ' : ''}workspace <ArrowRight size={16} />
            </Link>
            <Link to={backUrl} className="context-button context-button-secondary">
              {practiceId ? 'Return to clients' : 'All companies'}
            </Link>
          </div>
        </main>
      ) : (
        <main className="context-setup-main">
          <div className="context-setup-intro">
            <p className="context-eyebrow">
              {isExisting
                ? 'Refine company context'
                : practiceId
                  ? 'New client workspace'
                  : 'New company workspace'}
            </p>
            <h1>
              {isExisting
                ? 'Keep your company understanding current.'
                : 'A better strategy starts with understanding your company.'}
            </h1>
            <p>
              Bring your website and documents. We’ll pull together the useful details, ask about
              the gaps, and build a company brief you can review.
            </p>
          </div>
          <ol className="context-stepper" aria-label="Company setup progress">
            {steps.map((label, index) => (
              <li
                key={label}
                className={`${step === index + 1 ? 'is-current' : step > index + 1 ? 'is-complete' : ''}`}
                aria-current={step === index + 1 ? 'step' : undefined}
              >
                <button
                  type="button"
                  aria-label={`Go to step ${index + 1}: ${label}`}
                  disabled={busy || index + 1 >= step}
                  onClick={() =>
                    void perform(async () => {
                      goStep(index + 1);
                    })
                  }
                >
                  <span>{step > index + 1 ? <Check size={15} /> : index + 1}</span>
                  <div>
                    <small>Step {index + 1}</small>
                    <strong>{label}</strong>
                  </div>
                </button>
              </li>
            ))}
          </ol>
          {error && (
            <div className="context-error context-page-error" role="alert">
              {error}{' '}
              {draft && error.toLowerCase().includes('refresh') && (
                <button
                  type="button"
                  onClick={() =>
                    void perform(async (current) => {
                      const latest = await companyOnboardingApi.get(current.id);
                      dirty.current = false;
                      setSaving('saved');
                      setConfirmed(false);
                      accept(latest, true);
                    }, false)
                  }
                >
                  Reload saved draft
                </button>
              )}{' '}
              {saving === 'failed' && (
                <button type="button" onClick={() => void perform(async () => undefined)}>
                  Retry save
                </button>
              )}
            </div>
          )}
          {!draft ? (
            <section className="context-panel context-loading">
              {error ? (
                <>
                  <button
                    type="button"
                    className="context-button"
                    onClick={() => setAttempt((value) => value + 1)}
                  >
                    Retry loading draft
                  </button>
                  {savedDrafts.length > 0 && (
                    <div className="context-saved-drafts">
                      <h2>Your saved drafts</h2>
                      <p>Resume a draft, or remove one you no longer need to make room.</p>
                      {savedDrafts.map((saved) => (
                        <div key={saved.id}>
                          <strong>{saved.profile.name || 'Untitled company'}</strong>
                          <button
                            type="button"
                            className="context-button context-button-secondary context-button-small"
                            onClick={() => {
                              if (saved.mode === 'existing' && saved.companyId) {
                                navigate(`/company/${saved.companyId}/context`);
                                return;
                              }
                              setError('');
                              dirty.current = false;
                              accept(saved, true);
                              setParams(
                                (current) => {
                                  current.set('draft', saved.id);
                                  if (saved.practiceId) current.set('practice', saved.practiceId);
                                  else current.delete('practice');
                                  return current;
                                },
                                { replace: true }
                              );
                            }}
                          >
                            Resume draft
                          </button>
                          <button
                            type="button"
                            className="context-text-button"
                            onClick={async () => {
                              try {
                                await companyOnboardingApi.remove(saved.id);
                                setSavedDrafts((current) =>
                                  current.filter((item) => item.id !== saved.id)
                                );
                              } catch (err) {
                                setError(
                                  err instanceof Error ? err.message : 'Unable to remove draft.'
                                );
                              }
                            }}
                          >
                            Remove draft
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Loader2 size={24} className="context-spin" />
                  <p role="status">Opening your company draft…</p>
                </>
              )}
            </section>
          ) : (
            <div className="context-setup-grid">
              <section className="context-panel context-step-panel">
                <div className="context-panel-heading">
                  <div>
                    <p className="context-eyebrow">
                      {step === 1
                        ? 'Start with what you have'
                        : step === 2
                          ? 'A focused conversation'
                          : 'Make it yours'}
                    </p>
                    <h2>
                      {step === 1
                        ? 'Tell us where to look.'
                        : step === 2
                          ? 'Let’s understand what matters.'
                          : 'Review your company brief.'}
                    </h2>
                  </div>
                  <span
                    className={`context-save-status ${saving === 'failed' ? 'is-failed' : ''}`}
                    role="status"
                  >
                    {saving === 'saving'
                      ? 'Saving…'
                      : saving === 'pending'
                        ? 'Unsaved changes'
                        : saving === 'failed'
                          ? 'Save failed'
                          : 'Draft saved'}
                  </span>
                </div>
                {researching && step !== 1 && (
                  <div className="context-research-status" role="status">
                    <Loader2 size={21} className="context-spin" />
                    <div>
                      <strong>Reading your company sources</strong>
                      <p>
                        {isExisting
                          ? 'Your approved context stays active while this draft is prepared.'
                          : 'Your company draft is saved while we prepare its brief.'}
                      </p>
                    </div>
                  </div>
                )}
                {step === 1 && (
                  <>
                    <label className="context-website-label">
                      Company website
                      <input
                        value={profile.website}
                        onChange={(event) =>
                          changeProfile({ ...profile, website: event.target.value })
                        }
                        placeholder="https://yourcompany.com"
                        inputMode="url"
                        disabled={busy || researching}
                      />
                      <Globe2 size={19} />
                    </label>
                    <p className="context-help">
                      We’ll prioritise up to 100 useful public pages, including what you do, who you
                      serve and what makes your company different.
                    </p>
                    <div className="context-divider">
                      <span>And add any documents you have</span>
                    </div>
                    <SourceLibrary
                      sources={draft.sources}
                      capabilities={capabilities}
                      busy={busy || researching}
                      onUpload={async (files) => {
                        await perform(async (current) => {
                          let next = current;
                          for (const file of files) {
                            next = await companyOnboardingApi.upload(next.id, next.revision, file);
                            accept(next);
                          }
                          return next;
                        });
                      }}
                      onRemove={async (source) => {
                        await perform((current) =>
                          companyOnboardingApi.removeSource(current.id, source.id, current.revision)
                        );
                      }}
                    />
                    {(researching || draft.researchRuns > 0) && (
                      <ResearchProgress
                        draft={draft}
                        busy={busy}
                        onCancel={() =>
                          void perform(async (current) => {
                            const next = await companyOnboardingApi.cancel(
                              current.id,
                              current.revision
                            );
                            setManual(true);
                            goStep(2);
                            return next;
                          }, false)
                        }
                      />
                    )}
                    {draft.warnings.length > 0 && (
                      <div className="context-notice" role="status">
                        {draft.warnings.map((warning, index) => (
                          <p key={index}>{warning}</p>
                        ))}
                      </div>
                    )}
                    <div className="context-actions">
                      {draft.researchRuns === 0 &&
                      (profile.website.trim() ||
                        draft.sources.some((source) => source.kind === 'document')) ? (
                        <button
                          type="button"
                          className="context-button"
                          disabled={busy || researching}
                          onClick={() =>
                            void perform((current) =>
                              companyOnboardingApi.research(
                                current.id,
                                current.revision,
                                profile.website
                              )
                            )
                          }
                        >
                          <Sparkles size={17} />{' '}
                          {busy
                            ? 'Preparing…'
                            : researching
                              ? 'Reading sources…'
                              : 'Build my company brief'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="context-button"
                          disabled={busy || researching}
                          onClick={() => void startInterview()}
                        >
                          Continue <ArrowRight size={16} />
                        </button>
                      )}
                      {draft.researchRuns === 0 && (
                        <button
                          type="button"
                          className="context-text-button"
                          disabled={busy || researching}
                          onClick={() =>
                            void perform(async () => {
                              setManual(true);
                              goStep(2);
                            })
                          }
                        >
                          Start with my own description
                        </button>
                      )}
                      {draft.researchRuns > 0 &&
                        draft.researchRuns < 2 &&
                        (profile.website.trim() ||
                          draft.sources.some((source) => source.kind === 'document')) && (
                          <button
                            type="button"
                            className="context-button context-button-secondary"
                            disabled={busy || researching}
                            onClick={() =>
                              void perform((current) =>
                                companyOnboardingApi.research(
                                  current.id,
                                  current.revision,
                                  profile.website
                                )
                              )
                            }
                          >
                            <Sparkles size={16} /> Update brief from sources
                          </button>
                        )}
                    </div>
                  </>
                )}
                {step === 2 && (
                  <>
                    <div className="context-interview-meta">
                      <span>
                        Question{' '}
                        {Math.min(draft.interview.answeredCount + 1, draft.interview.maxQuestions)}{' '}
                        of up to {draft.interview.maxQuestions}
                      </span>
                      <span>You can finish whenever you’re ready</span>
                    </div>
                    {!manual && draft.interview.currentQuestion && !draft.interview.complete ? (
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          if (!answer.trim()) return;
                          void perform((current) =>
                            companyOnboardingApi.answer(
                              current.id,
                              current.revision,
                              current.interview.currentQuestion!.id,
                              answer
                            )
                          );
                        }}
                      >
                        <QuestionCard
                          key={draft.interview.currentQuestion.id}
                          question={draft.interview.currentQuestion}
                          value={{
                            answer,
                            choiceId: draft.interview.currentQuestion.choices?.find(
                              (choice) => choice.label === answer
                            )?.id,
                          }}
                          onChange={(value) => {
                            const nextAnswer = value.choiceId
                              ? draft.interview.currentQuestion!.choices?.find(
                                  (choice) => choice.id === value.choiceId
                                )?.label || ''
                              : value.answer;
                            setAnswer(nextAnswer);
                            pendingAnswerRef.current = {
                              questionId: draft.interview.currentQuestion!.id,
                              value: nextAnswer,
                            };
                            editVersion.current += 1;
                            dirty.current = true;
                            setSaving('pending');
                          }}
                          disabled={busy || researching}
                        />
                        <div className="context-actions">
                          <button
                            type="submit"
                            className="context-button"
                            disabled={busy || researching || !answer.trim()}
                          >
                            {busy ? <Loader2 size={16} className="context-spin" /> : null} Save
                            answer <ArrowRight size={16} />
                          </button>
                          <button
                            type="button"
                            className="context-text-button"
                            disabled={busy || researching}
                            onClick={() =>
                              void perform((current) =>
                                companyOnboardingApi.answer(
                                  current.id,
                                  current.revision,
                                  current.interview.currentQuestion!.id,
                                  '',
                                  true
                                )
                              )
                            }
                          >
                            Skip / I don’t know
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="context-interview-complete">
                        <CheckCircle2 size={30} />
                        <h3>
                          {draft.interview.complete
                            ? 'You’ve covered the important gaps.'
                            : 'Add the essentials in your own words.'}
                        </h3>
                        <p>
                          Review your brief and add any details that will help future strategy
                          suggestions feel specific to your company.
                        </p>
                        <ProfileEditor
                          profile={profile}
                          onChange={changeProfile}
                          disabled={busy || researching}
                          facts={draft.facts}
                          sources={draft.sources}
                        />
                      </div>
                    )}
                    <div className="context-step-footer">
                      <button
                        type="button"
                        className="context-text-button"
                        disabled={busy}
                        onClick={() =>
                          void perform(async () => {
                            goStep(1);
                          })
                        }
                      >
                        <ArrowLeft size={15} /> Back to sources
                      </button>
                      <button
                        type="button"
                        className="context-button context-button-secondary"
                        disabled={busy || researching}
                        onClick={() =>
                          void perform(async (current) => {
                            if (!manual && answer.trim() && current.interview.currentQuestion) {
                              const next = await companyOnboardingApi.answer(
                                current.id,
                                current.revision,
                                current.interview.currentQuestion.id,
                                answer
                              );
                              goStep(3);
                              return next;
                            }
                            goStep(3);
                          })
                        }
                      >
                        Finish and review <ArrowRight size={16} />
                      </button>
                    </div>
                  </>
                )}
                {step === 3 && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!validProfile || !confirmed) return;
                      void perform(async (current) => {
                        if (onApprove) {
                          await onApprove(current);
                          return;
                        }
                        const completion = await companyOnboardingApi.complete(
                          current.id,
                          current.revision,
                          practiceId ? ownerEmail : undefined
                        );
                        return applyOnboardingCompletion(current, completion);
                      });
                    }}
                  >
                    <p className="context-help context-review-help">
                      Correct anything that needs attention. Details you leave blank stay unknown;
                      existing mission, vision and values remain company facts for future planning.
                    </p>
                    <ProfileEditor
                      profile={profile}
                      onChange={changeProfile}
                      disabled={busy || researching}
                      facts={draft.facts}
                      sources={draft.sources}
                    />
                    {practiceId && !isExisting && (
                      <label className="context-owner-email">
                        Client owner’s email <span className="context-required">Required</span>
                        <input
                          type="email"
                          required
                          value={ownerEmail}
                          onChange={(event) => {
                            ownerEmailRef.current = event.target.value;
                            setOwnerEmail(event.target.value);
                            editVersion.current += 1;
                            dirty.current = true;
                            setSaving('pending');
                            setConfirmed(false);
                          }}
                          placeholder="owner@clientcompany.com"
                          disabled={busy || researching}
                          maxLength={255}
                          autoComplete="email"
                        />
                        <small>
                          The client accepts an invitation to take direct ownership of their
                          company.
                        </small>
                      </label>
                    )}
                    <label className="context-confirm">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(event) => setConfirmed(event.target.checked)}
                        disabled={busy || researching}
                      />
                      <span>I’ve reviewed this brief and it accurately describes the company.</span>
                    </label>
                    <div className="context-step-footer">
                      <button
                        type="button"
                        className="context-text-button"
                        disabled={busy}
                        onClick={() => goStep(2)}
                      >
                        <ArrowLeft size={15} /> Back to questions
                      </button>
                      <button
                        type="submit"
                        className="context-button"
                        disabled={busy || researching || !validProfile || !confirmed}
                      >
                        {busy ? (
                          <Loader2 size={16} className="context-spin" />
                        ) : (
                          <CheckCircle2 size={16} />
                        )}
                        {isExisting
                          ? 'Approve company brief'
                          : practiceId
                            ? 'Create client & prepare invitation'
                            : 'Create company workspace'}
                      </button>
                    </div>
                  </form>
                )}
              </section>
              <BusinessBrief
                collapsible
                profile={profile}
                onChange={changeProfile}
                facts={draft.facts}
                sources={draft.sources}
                editable={!busy && !researching}
              />
            </div>
          )}
        </main>
      )}
    </div>
  );
}
